import { Inject, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';

import { Survey } from '@lyra/app/pages/questionnaire/core/classes/survey.class';
import { ISnackBarData, SnackBarComponent } from '@lyra/app/shared/components/snack-bar/snack-bar.component';
import { Environment } from '@lyra/environments/env';
import { MainApi } from '@mark/web-contracts';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { CookieService } from 'ngx-cookie-service';
import { Observable, zip } from 'rxjs';
import { catchError, filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';

import { SurveyBhUserGrpcContractService } from '../../api/grpc';
import { ICreateSimpleOrder, OrderV2GrpcContractService } from '../../api/grpc/services/order-v2.service';
import { SubstanceUserGrpcContractService } from '../../api/grpc/services/substance-user.service';
import { PaymentStatus } from '../../enums/payment-status';
import { RouteString } from '../../enums/route-string';
import { DateHelper } from '../../functions/date.helper';
import { ENVIRONMENT } from '../../providers/environment.provider';
import { SELECTED_LANG } from '../../providers/selected-lang.provider';
import { ErrorService } from '../../services/error.service';
import * as actions from '../actions/plan.actions';
import { PlanSelectService } from '../selectors';
import { IAppState } from '../states/app.state';

@Injectable()
export class PlanEffects {
  onSetSurvey$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(actions.SET_SURVEY),
        tap(({ withRedirection }) => {
          this._store$.dispatch(actions.GET_SUBSTANCES_COMMAND());
          if (withRedirection) {
            this._store$.dispatch(actions.TO_PLAN());
          }
        }),
      ),
    { dispatch: false },
  );

  toAnalyzing$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(actions.TO_ANALYZING),
        withLatestFrom(this._locale$),
        tap(([, locale]) => {
          this._router.navigate(['/', locale, RouteString.ANALYZING], {
            relativeTo: this._activatedRoute,
            queryParamsHandling: 'merge',
            // change route without adding empty rout to the history (like redirectTo)
            replaceUrl: true,
          });
        }),
      ),
    { dispatch: false },
  );

  toPlan$ = createEffect(
    () =>
      zip(this._actions$.pipe(ofType(actions.TO_PLAN)), this._actions$.pipe(ofType(actions.ANALYZING_OVER))).pipe(
        withLatestFrom(this._locale$),
        tap(([, locale]) => {
          this._router.navigate(['/', locale, RouteString.PLAN], {
            relativeTo: this._activatedRoute,
            queryParamsHandling: 'merge',
            // change route without adding empty rout to the history (like redirectTo)
            replaceUrl: true,
          });
        }),
      ),
    { dispatch: false },
  );

  toOrder$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(actions.TO_ORDER),
        withLatestFrom(this._locale$),
        tap(([, locale]) => {
          // if (realUser) {
          //   this._router.navigate(['/', locale, RouteString.ORDER], {
          //     relativeTo: this._activatedRoute,
          //     queryParamsHandling: 'merge',
          //   });
          // }
          this._router.navigate(['/', locale, RouteString.ORDER], {
            relativeTo: this._activatedRoute,
            queryParamsHandling: 'merge',
          });
        }),
      ),
    { dispatch: false },
  );

  getSubstances$ = createEffect(() =>
    this._actions$.pipe(
      ofType(actions.GET_SUBSTANCES_COMMAND),
      withLatestFrom(this._planSelect.isSubstancesLoading()),
      filter(([, loading]) => !loading),
      switchMap(() =>
        this._substanceUserGrpcContractService.getLastCalculated().pipe(
          map(({ Substances }) => actions.GET_SUBSTANCES_SUCCESS({ substances: Substances ?? [] })),
          catchError((response) =>
            this._errorService.handleApiError(response, 'SubstanceUserGrpcContractClient.GetLastCalculated'),
          ),
        ),
      ),
    ),
  );

  getLastCompletedSurvey$ = createEffect(() =>
    this._actions$.pipe(
      ofType(actions.GET_LAST_COMPLETED),
      withLatestFrom(this._planSelect.isSubstancesLoading()),
      filter(([, loading]) => !loading),
      switchMap(() =>
        this._surveyBhUserGrpcContractService.getLastCompleted().pipe(
          tap((survey) => {
            this._store$.dispatch(actions.SET_SURVEY({ survey: new Survey(survey), withRedirection: false }));
          }),
          catchError((response) =>
            this._errorService.handleApiError(response, 'SurveyBhUserWebGrpcContractClient.GetLastCompleted'),
          ),
        ),
      ),
    ),
  );

  createOrder$ = createEffect(() =>
    this._actions$.pipe(
      ofType(actions.CREATE_ORDER_COMMAND),
      switchMap(({ formData, paymentType }) => {
        // Shipping / billing / appointment are identical for both order variants.
        let request: ICreateSimpleOrder = {
          AppointmentAt: DateHelper.DateTimeUnitFromDate(new Date(Date.now())),
          ServiceIds: ['00000000-0000-0000-0002-000000000000'],
          ServiceType: MainApi.OrderServiceType.OrderServiceType_Products,
          Shipping: {
            FirstName: formData.firstName ?? '',
            LastName: formData.lastName ?? '',
            PhoneNumber: formData.phone ?? '',
            Address: {
              Country: 'United States',
              State: formData.state ?? '',
              Street: formData.streetAddress ?? '',
              ZipCode: formData.zipCode ?? '',
              City: formData.city ?? '',
              Building: '',
              Name: '',
              Description: '',
            },
          },
          Billing: {
            CopyShippingAddress: true,
            Address: {},
          },
        };
        // 'short-quiz' and 'ali-flow' create a PayForward order; otherwise the default Products order.
        switch (paymentType) {
          case 'short-quiz':
            request = {
              ...request,
              ServiceIds: ['00000000-0000-0000-0003-000000000000'],
              ServiceType: MainApi.OrderServiceType.OrderServiceType_PayForward,
              CompanyId: '07334c1b-6bcb-44cb-888e-0bd34a0fff45',

              PayForwardSettings: {
                Title: 'custom text',
                AllowedServiceType: MainApi.OrderServiceType.OrderServiceType_PremiumBhSubstances,
                AllowedProduct: null as any,
              },
            };
            break;
          // 'ali-flow' duplicates the short-quiz PayForward order for now (will diverge later).
          case 'ali-flow':
            request = {
              ...request,
              ServiceIds: ['00000000-0000-0000-0003-000000000000'],
              ServiceType: MainApi.OrderServiceType.OrderServiceType_PayForward,
              CompanyId: '07334c1b-6bcb-44cb-888e-0bd34a0fff45',

              PayForwardSettings: {
                Title: 'custom text',
                AllowedServiceType: MainApi.OrderServiceType.OrderServiceType_PremiumBhSubstances,
                AllowedProduct: null as any,
              },
            };
            break;
          default:
            break;
        }
        return this._orderV2GrpcContractService.createSimpleOrder(request).pipe(
          map((order) => actions.CREATE_ORDER_SUCCESS({ order: order })),
          catchError((response) =>
            this._errorService.handleApiError(response, 'OrderV2GrpcContractClient.CreateSimpleOrder'),
          ),
        );
      }),
    ),
  );

  createOrderSuccess$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(actions.CREATE_ORDER_SUCCESS),
        filter(({ order }) => !!order),
        tap(({ order }) => {
          const currentHref = window.location.origin + window.location.pathname;
          const queryParams = new URLSearchParams({
            Language: 'en',
            OrderIds: [order.Id].join(','), // массив в строку через запятую
            ReturnUrl: currentHref,
          });
          /** 2121 2121 2121 2121 */
          window.location.href = `${this._env.checkoutURL}?${queryParams.toString()}`;
        }),
      ),
    { dispatch: false },
  );

  paymentStatus$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(actions.PAYMENT_STATUS),
        tap(({ status }) => {
          switch (status) {
            case PaymentStatus.Error:
              this._snackBar.openFromComponent(SnackBarComponent, {
                data: {
                  text: 'Oops! Something went wrong with your payment. Please try again.',
                  closeButton: true,
                  icon: { name: 'info' },
                  action: 'error',
                } as ISnackBarData,
                duration: 5000,
                verticalPosition: 'top',
                horizontalPosition: 'center',
                panelClass: ['mark-snackbar', 'error'],
              });
              break;
            case PaymentStatus.Cancel:
              this._snackBar.openFromComponent(SnackBarComponent, {
                data: {
                  text: 'Payment was cancelled. Please try again.',
                  closeButton: true,
                  icon: { name: 'info' },
                  action: 'error',
                } as ISnackBarData,
                duration: 5000,
                verticalPosition: 'top',
                horizontalPosition: 'center',
                panelClass: ['mark-snackbar', 'error'],
              });
              break;
            default:
              break;
          }
        }),
      ),
    { dispatch: false },
  );
  constructor(
    private _store$: Store<IAppState>,
    private _actions$: Actions,
    private _substanceUserGrpcContractService: SubstanceUserGrpcContractService,
    private _orderV2GrpcContractService: OrderV2GrpcContractService,
    private _errorService: ErrorService,
    private _router: Router,
    private _activatedRoute: ActivatedRoute,
    private _planSelect: PlanSelectService,
    private _snackBar: MatSnackBar,
    private _cookieService: CookieService,
    private _surveyBhUserGrpcContractService: SurveyBhUserGrpcContractService,
    @Inject(ENVIRONMENT) private _env: Environment,
    @Inject(SELECTED_LANG) private _locale$: Observable<string>,
  ) {}
}
